import { WhatsappLogo, Camera, ArrowRight, MapPin, Clock } from "@phosphor-icons/react";
import { BRAND, buildWhatsAppLink } from "../lib/brand";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 pb-16 sm:pt-32 sm:pb-24 overflow-hidden"
    >
      {/* Background image w/ overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.pexels.com/photos/5279317/pexels-photo-5279317.jpeg"
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-[var(--rc-bg)]" />
        <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_50%_0%,rgba(184,115,51,0.18),transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Eyebrow */}
        <div className="flex items-center gap-3 mb-6 rc-rise">
          <span className="inline-flex items-center gap-2 label-eyebrow">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--rc-copper-bright)] rc-pulse" />
            Ricatto Metais · Maringá-PR
          </span>
          <span className="hidden sm:inline-flex h-px w-16 bg-white/15" />
          <span className="hidden sm:inline-flex text-xs text-zinc-400 font-mono-ui">
            COMPRA · ALUMÍNIO · COBRE
          </span>
        </div>

        {/* Headline */}
        <h1
          data-testid="hero-headline"
          className="font-display font-bold text-white text-4xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight max-w-5xl rc-rise"
          style={{ animationDelay: "80ms" }}
        >
          Venda seu{" "}
          <span className="text-[var(--rc-copper-bright)]">alumínio</span> e{" "}
          <span className="text-[var(--rc-copper-bright)]">cobre</span> com{" "}
          <span className="underline decoration-[var(--rc-blue-bright)] decoration-[3px] underline-offset-[10px]">
            pagamento na hora
          </span>
          .
        </h1>

        {/* Subheadline */}
        <p
          className="mt-6 text-zinc-300 text-base sm:text-xl max-w-3xl leading-relaxed rc-rise"
          style={{ animationDelay: "160ms" }}
        >
          Atendimento rápido em <strong className="text-white">{BRAND.region}</strong>. Pesagem justa, balança aferida e
          avaliação direta pelo WhatsApp — basta enviar uma foto do material.
        </p>

        {/* CTAs */}
        <div
          className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 rc-rise"
          style={{ animationDelay: "240ms" }}
        >
          <a
            data-testid="hero-whatsapp-btn"
            href={buildWhatsAppLink(
              "Olá! Quero enviar a foto do meu material para avaliação.",
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="rc-btn-whatsapp inline-flex items-center justify-center gap-3 px-6 sm:px-8 py-4 sm:py-5 text-base sm:text-lg rounded-md group"
          >
            <WhatsappLogo size={26} weight="fill" />
            <span className="font-display font-bold">Enviar foto do material</span>
            <ArrowRight
              size={20}
              weight="bold"
              className="transition-transform group-hover:translate-x-1"
            />
          </a>
          <a
            data-testid="hero-secondary-btn"
            href="#avaliacao"
            className="rc-btn-ghost inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-4 sm:py-5 text-base sm:text-lg rounded-md"
          >
            <Camera size={20} weight="bold" />
            <span className="font-display font-bold">Pedir avaliação rápida</span>
          </a>
        </div>

        {/* Stats / trust strip */}
        <div
          className="mt-12 sm:mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 rc-rise"
          style={{ animationDelay: "320ms" }}
        >
          {[
            { k: "100%", v: "Pagamento na hora" },
            { k: "Aferida", v: "Balança certificada" },
            { k: "Rápida", v: "Retirada em Maringá" },
            { k: "+10 anos", v: "Compra de metais" },
          ].map((s) => (
            <div
              key={s.v}
              className="bg-[var(--rc-bg)] p-5 sm:p-7"
              data-testid={`hero-stat-${s.k}`}
            >
              <div className="font-display font-bold text-2xl sm:text-3xl text-white tracking-tight">
                {s.k}
              </div>
              <div className="mt-1 text-xs sm:text-sm text-zinc-400 uppercase tracking-wider font-mono-ui">
                {s.v}
              </div>
            </div>
          ))}
        </div>

        {/* Quick info row */}
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-400 font-mono-ui">
          <span className="inline-flex items-center gap-2">
            <MapPin size={16} weight="bold" className="text-[var(--rc-copper-bright)]" />
            {BRAND.addressLine}
          </span>
          <span className="inline-flex items-center gap-2">
            <Clock size={16} weight="bold" className="text-[var(--rc-copper-bright)]" />
            {BRAND.hours}
          </span>
        </div>
      </div>
    </section>
  );
}
